import React, { useState, useEffect, useRef } from "react";

type Option = { id: string; name: string };

interface MultiSelectProps {
  label?: string;
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  options,
  value,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close when clicked outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <div className="relative" ref={ref}>
      {label && <label className="block text-sm font-medium mb-1">{label}</label>}

      {/* Selected Chips */}
      <div
        className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex flex-wrap gap-2"
        onClick={() => setOpen((prev) => !prev)}
      >
        {value.length === 0 && (
          <span className="text-gray-400 text-sm">Select departments...</span>
        )}

        {value.map((id) => {
          const item = options.find((o) => o.id === id);
          if (!item) return null;
          return (
            <span
              key={id}
              className="bg-agentic-primary-blue/10 text-agentic-primary-blue px-2 py-1 rounded-md text-xs flex items-center gap-1"
            >
              {item.name}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(value.filter((v) => v !== id));
                }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </span>
          );
        })}
      </div>

      {/* Dropdown List */}
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-2">
          {options.map((o) => (
            <label
              key={o.id}
              className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
            >
              <input
                type="checkbox"
                checked={value.includes(o.id)}
                onChange={() => handleToggle(o.id)}
              />
              <span>{o.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
